import {
  Box,
  Button,
  Grid,
  InputLabel,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

// COMPONENTS
import ConfirmModal from '../../../../../common/modals/ConfirmModal';
import BackButton from '../../../../../common/BackButton';
import ProviderPicker from '../../../../../common/ProviderPicker';
import DeviceSelect from './DeviceSelect';

// NETWORK
import useCarrierRequest from '../../../../../../requests/CarrierRequest';

// BREADCRUMB
import { useBreadcrumb } from '../../BreadcrumbContext';

const CarrierDeviceSingleView = ({
  country,
  carrier,
  carrierDevice,
  errorHandler,
  navigate,
}) => {
  const { grandChildId } = useParams();
  const { addBreadcrumb, dropBreadcrumb } = useBreadcrumb();

  const [isEditing, setIsEditing] = useState(false);
  const [edits, setEdits] = useState({});
  const [selectedDevice, setSelectedDevice] = useState({});

  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);

  const {
    createNewCarrierDevice,
    updateCarrierDevice,
    deleteCarrierDeviceById,
  } = useCarrierRequest();

  useEffect(() => {
    setIsEditing(!grandChildId || grandChildId === '0');
    if (selectedDevice) {
      setSelectedDevice(carrierDevice || {});
    }

    addBreadcrumb(3, `Device: ${carrierDevice?.device_name || 'NEW'}`);
    return () => {
      dropBreadcrumb(3);
    };
  }, [carrierDevice]);

  useEffect(() => {
    if (selectedDevice && !edits.priority) {
      handleFormChange('priority', selectedDevice.priority);
    }
  }, [selectedDevice]);

  const toggleEdit = () => {
    setEdits({});

    if (!grandChildId || grandChildId === '0') {
      navigate(-1);
      return;
    }

    setIsEditing(!isEditing);
  };

  const handleFormChange = (name, newValue) => {
    setEdits({ ...edits, [name]: newValue });
  };

  const determineApplyProvider = () => {
    if (edits.provider_id) return 'this';
    if (carrierDevice.carrier_device_id) return 'this';
    return 'all';
  };

  const handleSubmit = async (e) => {
    try {
      e.preventDefault();
      const data = edits;

      const applyProvider = determineApplyProvider();

      if (carrierDevice.carrier_device_id) {
        await updateCarrierDevice(
          country.country_code_id,
          carrier.carrier_id,
          carrierDevice.carrier_device_id,
          data,
          applyProvider,
          errorHandler
        );
      } else {
        await createNewCarrierDevice(
          country.country_code_id,
          carrier.carrier_id,
          data,
          applyProvider,
          errorHandler
        );
      }
      navigate(-1);
    } catch (e) {
      console.log('Have error:', e);
    }
  };

  const confirmDelete = () => {
    setIsDeleting(true);
    setShowDeleteConfirmation(true);
  };

  const handleDelete = async () => {
    await deleteCarrierDeviceById(
      country.country_code_id,
      carrier.carrier_id,
      carrierDevice.carrier_device_id,
      carrierDevice.provider_id,
      errorHandler
    );
    setIsDeleting(false);
    navigate(-1);
  };

  const generateLander = () => (
    <Stack
      direction="row"
      gap={2}
      alignItems="center"
      sx={{ paddingBottom: 2 }}
    >
      <BackButton onClick={() => navigate(-1)} />
      <Typography variant="h4">Carrier Device Information</Typography>
    </Stack>
  );

  const generateBody = () => {
    if (!carrierDevice)
      return <Typography>(Carrier Device) No Data</Typography>;

    if (isEditing) {
      const isBaseProvider =
        parseInt(carrierDevice.copy_dlvry_from) ===
        parseInt(carrierDevice.provider_id);
      return (
        <Box component="form" onSubmit={handleSubmit}>
          <Grid container spacing={1} maxWidth={800}>
            <Grid size={12}>
              <Typography>
                <strong>Carrier:</strong> {carrierDevice.carrier_name}
              </Typography>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Device
                </InputLabel>
                <DeviceSelect
                  carrierDevice={carrierDevice}
                  countryId={country.country_code_id || 0}
                  carrierId={carrier.carrier_id || 0}
                  disabled={!!carrierDevice.carrier_device_id}
                  required
                  fullWidth
                  size="small"
                  onChange={(device) => {
                    setSelectedDevice(device);
                    handleFormChange('device_id', device.device_id);
                  }}
                  errorHandler={errorHandler}
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }}>Apply To:</InputLabel>
                <ProviderPicker
                  placeholder="All Providers"
                  providerId={
                    typeof edits.provider_id === 'number'
                      ? edits.provider_id
                      : carrierDevice?.provider_id || ''
                  }
                  optionName="provider_id"
                  baseOnly={!!carrierDevice.carrier_device_id && isBaseProvider}
                  disabled={
                    !!carrierDevice.carrier_device_id && !isBaseProvider
                  }
                  onChange={(_, newValue) => {
                    console.log('provider_id', newValue);
                    handleFormChange('provider_id', newValue?.id || 0);
                  }}
                  errorHandler={errorHandler}
                  fullWidth
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Stack
                sx={{
                  flexDirection: { md: 'row' },
                  alignItems: { md: 'center' },
                  gap: { md: 2 },
                }}
              >
                <InputLabel sx={{ minWidth: 100 }} required>
                  Priority:
                </InputLabel>
                <TextField
                  name="priority"
                  type="number"
                  value={edits.priority || carrierDevice.priority || 35}
                  fullWidth
                  size="small"
                  required
                  onInput={(e) =>
                    handleFormChange('priority', parseInt(e.target.value))
                  }
                />
              </Stack>
            </Grid>
            <Grid size={12}>
              <Box display="flex" gap={2}>
                <Button variant="text" color="secondary" onClick={toggleEdit}>
                  Cancel
                </Button>
                <Button variant="contained" color="secondary" type="submit">
                  Save
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      );
    }

    return (
      <Grid container spacing={2}>
        <Grid size={12}>
          <Typography>
            <strong>Carrier:</strong> {carrier.carrier_name}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography>
            <strong>Device:</strong> {carrierDevice.device_name}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography>
            <strong>Provider:</strong> {carrierDevice.provider_name}
          </Typography>
        </Grid>
        <Grid size={12}>
          <Typography>
            <strong>Priority:</strong> {carrierDevice.priority}
          </Typography>
        </Grid>

        <Grid size={12}>
          <Button variant="contained" color="secondary" onClick={toggleEdit}>
            Edit
          </Button>
          <Box sx={{ display: 'flex', justifyContent: 'end' }}>
            {!isDeleting && (
              <Button color="error" variant="contained" onClick={confirmDelete}>
                Delete
              </Button>
            )}
            {isDeleting && 'Deleting...'}
          </Box>
        </Grid>
      </Grid>
    );
  };

  return (
    <Box className="Home carrier-device">
      {generateLander()}
      {generateBody()}
      <ConfirmModal
        show={showDeleteConfirmation}
        onClose={() => {
          setShowDeleteConfirmation(false);
          setIsDeleting(false);
        }}
        onConfirm={handleDelete}
        message="Are you sure you want to delete this carrier device?"
      />
    </Box>
  );
};

export default CarrierDeviceSingleView;
